/**
 * AYU Dinacharya — Personalisierte Tagesroutine
 * Morgen / Mittag / Abend Routine mit anpassbaren Startzeiten
 */
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/contexts/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Sun,
  Sunrise,
  Moon,
  Leaf,
  RefreshCw,
  Loader2,
  Clock,
  Sparkles,
  ChevronDown,
  ChevronUp,
  Pencil,
  Check,
  Info,
} from "lucide-react";
import { ThumbsFeedback } from "@/components/app/ThumbsFeedback";
import { useState, useMemo, useCallback } from "react";
import { toast } from "sonner";

interface RoutineStep {
  time: string;
  title: string;
  description: string;
  duration: string;
}

interface Herb {
  name: string;
  effect: string;
  dosage: string;
  timing: string;
}

const SEASON_LABELS: Record<string, string> = {
  fruehling: "Frühling",
  sommer: "Sommer",
  herbst: "Herbst",
  winter: "Winter",
};

const SEASON_ICONS: Record<string, string> = {
  fruehling: "🌸",
  sommer: "☀️",
  herbst: "🍂",
  winter: "❄️",
};

/** Parse "HH:MM" → minutes since midnight */
function parseTime(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Minutes since midnight → "HH:MM" */
function formatTime(mins: number): string {
  const total = ((mins % 1440) + 1440) % 1440; // wrap around 24h
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

/** Shift all steps by an offset (in minutes) from the original first step */
function shiftSteps(steps: RoutineStep[], offsetMinutes: number): RoutineStep[] {
  if (steps.length === 0) return steps;
  return steps.map((step) => ({
    ...step,
    time: formatTime(parseTime(step.time) + offsetMinutes),
  }));
}

/** Time picker for a routine block start time */
function TimeAdjuster({
  label,
  icon,
  currentStart,
  originalStart,
  onChangeOffset,
}: {
  label: string;
  icon: React.ReactNode;
  currentStart: string;
  originalStart: string;
  onChangeOffset: (newStartTime: string) => void;
}) {
  const { t } = useI18n();
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(currentStart);

  const handleConfirm = () => {
    onChangeOffset(tempValue);
    setEditing(false);
    toast.success(t("dinacharya.zeitGeaendert", { label, time: tempValue }));
  };

  const hasChanged = currentStart !== originalStart;

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/60 border border-[#d4dcc4]">
      <div className="flex items-center gap-1.5 text-[#5a6b4a]">
        {icon}
        <span className="text-xs font-medium">{label}</span>
      </div>
      {editing ? (
        <div className="flex items-center gap-1 ml-auto">
          <input
            type="time"
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            className="text-sm font-semibold text-[#2d3a1e] bg-white border border-[#8ba668] rounded px-2 py-0.5 w-[90px] focus:outline-none focus:ring-1 focus:ring-[#6b7f4e]"
          />
          <button
            onClick={handleConfirm}
            className="p-1 rounded-full bg-[#6b7f4e] text-white hover:bg-[#5a6b3e] transition-colors"
          >
            <Check className="w-3.5 h-3.5" />
          </button>
        </div>
      ) : (
        <button
          onClick={() => {
            setTempValue(currentStart);
            setEditing(true);
          }}
          className="flex items-center gap-1.5 ml-auto group"
        >
          <span className={`text-sm font-semibold ${hasChanged ? "text-[#8ba668]" : "text-[#2d3a1e]"}`}>
            {currentStart} {t("dinacharya.uhr")}
          </span>
          <Pencil className="w-3 h-3 text-[#8a9680] group-hover:text-[#6b7f4e] transition-colors" />
        </button>
      )}
    </div>
  );
}

function RoutineSection({
  title,
  icon,
  steps,
  color,
}: {
  title: string;
  icon: React.ReactNode;
  steps: RoutineStep[];
  color: string;
}) {
  const { t } = useI18n();
  const [expanded, setExpanded] = useState(true);

  return (
    <Card className={`border-l-4 ${color} bg-white/80 backdrop-blur-sm`}>
      <CardHeader
        className="cursor-pointer pb-2"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {icon}
            <CardTitle className="text-lg text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
              {title}
            </CardTitle>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-[#8a9680]">{t("dinacharya.schritte", { n: String(steps.length) })}</span>
            {expanded ? <ChevronUp className="w-4 h-4 text-[#8a9680]" /> : <ChevronDown className="w-4 h-4 text-[#8a9680]" />}
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0 space-y-3">
          {steps.map((step, i) => (
            <div key={i} className="flex gap-4 p-3 rounded-lg bg-[#f8f8f8]/60 hover:bg-[#f8f8f8] transition-colors">
              <div className="flex flex-col items-center min-w-[60px]">
                <span className="text-sm font-semibold text-[#6b7f4e]">{step.time}</span>
                <span className="text-[10px] text-[#8a9680] mt-0.5">{step.duration}</span>
              </div>
              <div className="flex-1">
                <h4 className="font-medium text-[#2d3a1e] text-sm">{step.title}</h4>
                <p className="text-xs text-[#5a6b4a] mt-1 leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </CardContent>
      )}
    </Card>
  );
}

function HerbCard({ herb }: { herb: Herb }) {
  return (
    <div className="p-3 rounded-lg bg-[#eef2e6] border border-[#d4dcc4] hover:shadow-md transition-shadow">
      <div className="flex items-center gap-2 mb-1">
        <Leaf className="w-4 h-4 text-[#6b7f4e]" />
        <span className="font-medium text-sm text-[#2d3a1e]">{herb.name}</span>
      </div>
      <p className="text-xs text-[#5a6b4a] mb-1">{herb.effect}</p>
      <div className="flex items-center gap-3 text-[10px] text-[#8a9680]">
        <span>Dosierung: {herb.dosage}</span>
        <span>•</span>
        <span>{herb.timing}</span>
      </div>
    </div>
  );
}

/** Storage key for time offsets */
const OFFSETS_KEY = "ayu-dinacharya-offsets";

interface TimeOffsets {
  morning: number; // offset in minutes
  midday: number;
  evening: number;
}

function loadOffsets(clientId: string): TimeOffsets {
  try {
    const raw = localStorage.getItem(`${OFFSETS_KEY}-${clientId}`);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { morning: 0, midday: 0, evening: 0 };
}

function saveOffsets(clientId: string, offsets: TimeOffsets) {
  localStorage.setItem(`${OFFSETS_KEY}-${clientId}`, JSON.stringify(offsets));
}

export default function AppDinacharya() {
  const { t } = useI18n();
  const { client } = useClientAuth();
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: activePlan, isLoading, refetch } = trpc.dinacharya.getActive.useQuery(
    { clientId: client?.clientId ?? "" },
    { enabled: !!client?.clientId }
  );

  const generateMutation = trpc.dinacharya.generate.useMutation({
    onSuccess: () => {
      refetch();
      toast.success(t("dinacharya.routineErstellt"));
      setIsGenerating(false);
    },
    onError: (err) => {
      toast.error(t("dinacharya.erstellenFehler", { msg: err.message }));
      setIsGenerating(false);
    },
  });

  const handleGenerate = () => {
    if (!client?.clientId) return;
    setIsGenerating(true);
    generateMutation.mutate({ clientId: client.clientId });
  };

  // Original steps from the plan
  const morningOrig = useMemo(() => (activePlan?.morningRoutine as RoutineStep[]) ?? [], [activePlan]);
  const middayOrig = useMemo(() => (activePlan?.middayRoutine as RoutineStep[]) ?? [], [activePlan]);
  const eveningOrig = useMemo(() => (activePlan?.eveningRoutine as RoutineStep[]) ?? [], [activePlan]);
  const herbs = useMemo(() => (activePlan?.herbs as Herb[]) ?? [], [activePlan]);

  // Original start times (first step of each block)
  const morningOrigStart = morningOrig[0]?.time ?? "06:00";
  const middayOrigStart = middayOrig[0]?.time ?? "12:00";
  const eveningOrigStart = eveningOrig[0]?.time ?? "19:00";

  // Time offsets (persisted in localStorage)
  const [offsets, setOffsets] = useState<TimeOffsets>(() =>
    loadOffsets(client?.clientId ?? "")
  );

  const updateOffset = useCallback(
    (block: keyof TimeOffsets, newStartTime: string) => {
      const origStart = block === "morning" ? morningOrigStart : block === "midday" ? middayOrigStart : eveningOrigStart;
      const offsetMins = parseTime(newStartTime) - parseTime(origStart);
      const newOffsets = { ...offsets, [block]: offsetMins };
      setOffsets(newOffsets);
      if (client?.clientId) saveOffsets(client.clientId, newOffsets);
    },
    [offsets, morningOrigStart, middayOrigStart, eveningOrigStart, client?.clientId]
  );

  // Shifted steps
  const morning = useMemo(() => shiftSteps(morningOrig, offsets.morning), [morningOrig, offsets.morning]);
  const midday = useMemo(() => shiftSteps(middayOrig, offsets.midday), [middayOrig, offsets.midday]);
  const evening = useMemo(() => shiftSteps(eveningOrig, offsets.evening), [eveningOrig, offsets.evening]);

  // Current start times (after offset)
  const morningCurrentStart = morning[0]?.time ?? morningOrigStart;
  const middayCurrentStart = midday[0]?.time ?? middayOrigStart;
  const eveningCurrentStart = evening[0]?.time ?? eveningOrigStart;

  const hasAnyOffset = offsets.morning !== 0 || offsets.midday !== 0 || offsets.evening !== 0;

  const resetOffsets = () => {
    const reset = { morning: 0, midday: 0, evening: 0 };
    setOffsets(reset);
    if (client?.clientId) saveOffsets(client.clientId, reset);
    toast.success(t("dinacharya.zeitZurueckgesetzt"));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f8f8]">
        <Loader2 className="w-8 h-8 animate-spin text-[#6b7f4e]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f8f8] p-4 lg:p-8">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("dinacharya.title")}
          </h1>
          <p className="text-[#5a6b4a] text-sm">
            {t("dinacharya.subtitle")}
          </p>
          {activePlan && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#eef2e6] border border-[#d4dcc4] text-xs text-[#5a6b4a]">
              <span>{SEASON_ICONS[activePlan.season] ?? "🌿"}</span>
              <span>{t("dinacharya.routineLabel", { season: SEASON_LABELS[activePlan.season] ?? activePlan.season })}</span>
            </div>
          )}
        </div>

        {/* Mantra */}
        {activePlan?.dailyMantra && (
          <Card className="bg-gradient-to-r from-[#8ba668]/10 to-[#6b7f4e]/10 border-[#8ba668]/30">
            <CardContent className="py-4 text-center">
              <Sparkles className="w-5 h-5 text-[#8ba668] mx-auto mb-2" />
              <p className="text-sm italic text-[#2d3a1e] leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
                "{activePlan.dailyMantra}"
              </p>
            </CardContent>
          </Card>
        )}

        {!activePlan ? (
          /* Kein Plan vorhanden */
          <Card className="bg-white/80 backdrop-blur-sm">
            <CardContent className="py-12 text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-[#eef2e6] flex items-center justify-center mx-auto">
                <Sun className="w-8 h-8 text-[#6b7f4e]" />
              </div>
              <h2 className="text-xl font-semibold text-[#2d3a1e]" style={{ fontFamily: "'Playfair Display', serif" }}>
                {t("dinacharya.keinePlan")}
              </h2>
              <p className="text-sm text-[#5a6b4a] max-w-md mx-auto">
                {t("dinacharya.keinePlanDesc")}
              </p>
              <Button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="bg-[#6b7f4e] hover:bg-[#5a6b3e] text-white"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {t("dinacharya.wirdErstellt")}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t("dinacharya.erstellen")}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        ) : (
          /* Plan vorhanden */
          <>
            {/* Zeiten anpassen */}
            <Card className="bg-white/80 backdrop-blur-sm border-[#d4dcc4]">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-[#6b7f4e]" />
                    <CardTitle className="text-sm font-medium text-[#2d3a1e]">
                      {t("dinacharya.startzeiten")}
                    </CardTitle>
                  </div>
                  {hasAnyOffset && (
                    <button
                      onClick={resetOffsets}
                      className="text-[10px] text-[#8a9680] hover:text-[#6b7f4e] underline transition-colors"
                    >
                      {t("dinacharya.zuruecksetzen")}
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-[#8a9680] mt-1 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  {t("dinacharya.startzeitenHinweis")}
                </p>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <TimeAdjuster
                    label={t("dinacharya.morgenLabel")}
                    icon={<Sunrise className="w-3.5 h-3.5 text-amber-500" />}
                    currentStart={morningCurrentStart}
                    originalStart={morningOrigStart}
                    onChangeOffset={(ts) => updateOffset("morning", ts)}
                  />
                  <TimeAdjuster
                    label={t("dinacharya.mittagLabel")}
                    icon={<Sun className="w-3.5 h-3.5 text-orange-500" />}
                    currentStart={middayCurrentStart}
                    originalStart={middayOrigStart}
                    onChangeOffset={(ts) => updateOffset("midday", ts)}
                  />
                  <TimeAdjuster
                    label={t("dinacharya.abendLabel")}
                    icon={<Moon className="w-3.5 h-3.5 text-indigo-400" />}
                    currentStart={eveningCurrentStart}
                    originalStart={eveningOrigStart}
                    onChangeOffset={(ts) => updateOffset("evening", ts)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Routine-Sektionen */}
            <RoutineSection
              title={t("dinacharya.morgen")}
              icon={<Sunrise className="w-5 h-5 text-amber-500" />}
              steps={morning}
              color="border-l-amber-400"
            />
            <RoutineSection
              title={t("dinacharya.mittag")}
              icon={<Sun className="w-5 h-5 text-orange-500" />}
              steps={midday}
              color="border-l-orange-400"
            />
            <RoutineSection
              title={t("dinacharya.abend")}
              icon={<Moon className="w-5 h-5 text-indigo-400" />}
              steps={evening}
              color="border-l-indigo-400"
            />

            {/* Kräuter-Empfehlungen */}
            {herbs.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-[#2d3a1e] flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                  <Leaf className="w-5 h-5 text-[#6b7f4e]" />
                  {t("dinacharya.kraeuterEmpfehlungen")}
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {herbs.map((herb, i) => (
                    <HerbCard key={i} herb={herb} />
                  ))}
                </div>
              </div>
            )}

            {/* Feedback — Daumen hoch/runter */}
            {client?.clientId && (
              <div className="flex items-center justify-center gap-2 pt-2">
                <span className="text-xs text-[#8a9680]">{t("dinacharya.wieRoutine")}</span>
                <ThumbsFeedback
                  clientId={client.clientId}
                  sessionType="routine"
                  context="/app/tagesroutine"
                  variant="minimal"
                />
              </div>
            )}

            {/* Neu generieren */}
            <div className="text-center pt-4">
              <Button
                variant="outline"
                onClick={handleGenerate}
                disabled={isGenerating}
                className="border-[#6b7f4e] text-[#6b7f4e] hover:bg-[#6b7f4e]/10"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {t("dinacharya.wirdAktualisiert")}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t("dinacharya.neuGenerieren")}
                  </>
                )}
              </Button>
              <p className="text-[10px] text-[#8a9680] mt-2">
                {t("dinacharya.erstelltAm", { date: new Date(activePlan.createdAt).toLocaleDateString("de-DE") })}
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
